import type {
  DSLSchemaResponse,
  EntitySchema,
  FieldDefinition,
} from '@commenda/types/src/commenda-os-dsl';
import { SWRFetcher, fetcher } from '@commenda/utils';
import { useApiScope } from '../scope';
import useSWR from 'swr';

const BASE_URL = 'dsl';

// Cache for entity schemas to avoid repeated fetches
const schemaCache = new Map<string, EntitySchema>();

/**
 * Fetch entity schema (non-hook version for use in callbacks)
 *
 * @param entityName - The entity name (e.g., 'corporation', 'company')
 * @returns The schema for the specified entity
 */
export async function fetchEntitySchema(
  entityName: string,
  shareToken: string | undefined
): Promise<EntitySchema | null> {
  // Scope belongs in the key: a schema fetched for one share must not be served to
  // another caller, and a stale hit would also mask a missing token.
  const cacheKey = shareToken ? `share:${shareToken}:${entityName}` : entityName;

  if (schemaCache.has(cacheKey)) {
    return schemaCache.get(cacheKey)!;
  }

  try {
    const response = await fetcher(`${BASE_URL}/schema/${entityName}`, {
      method: 'GET',
      shareToken,
    });
    const data = (await response.json()) as { entity: EntitySchema };
    if (data?.entity) {
      schemaCache.set(cacheKey, data.entity);
      return data.entity;
    }
    return null;
  } catch (err) {
    console.error(`Failed to fetch schema for ${entityName}:`, err);
    return null;
  }
}

/**
 * Find a field definition by its read path in the schema
 *
 * @param fields - The fields object from the schema
 * @param readPath - The read path to search for
 * @returns The field definition if found
 */
export function findFieldByReadPath(
  fields: Record<string, FieldDefinition>,
  readPath: string
): FieldDefinition | null {
  let matchedField: FieldDefinition | null = null;
  Object.entries(fields).some(([, field]) => {
    // Check if this field's read path matches
    if (field.read?.path === readPath) {
      matchedField = field;
      return true;
    }

    // Recursively search nested fields
    if (field.type === 'object' && field.fields) {
      const found = findFieldByReadPath(field.fields, readPath);
      if (found) {
        matchedField = found;
        return true;
      }
    }
    return false;
  });
  return matchedField;
}

/**
 * Hook to fetch the complete Commenda OS DSL schema
 *
 * @returns The full schema with all entities, fields, and operations
 */
export const useCommendaOSSchema = () => {
  const { data, error, isLoading, mutate } = useSWR<DSLSchemaResponse>(
    `${BASE_URL}/schema`,
    SWRFetcher({
      method: 'GET',
    }),
    {
      // Schema rarely changes, cache for longer
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // 1 minute
    }
  );

  return {
    schema: data ?? null,
    entities: data?.entities ?? {},
    error,
    isLoading,
    mutate,
  };
};

/**
 * Response wrapper from the entity schema endpoint
 */
interface EntitySchemaResponse {
  entity: EntitySchema;
}

/**
 * Hook to fetch schema for a specific entity
 *
 * @param entityName - The entity name (e.g., 'corporation', 'company')
 * @returns The schema for the specified entity
 */
export const useCommendaOSEntitySchema = (
  entityName: string | null | undefined
) => {
  const { shareToken } = useApiScope();

  const url = entityName ? `${BASE_URL}/schema/${entityName}` : null;

  const { data, error, isLoading, mutate } = useSWR<EntitySchemaResponse>(
    url,
    SWRFetcher({
      method: 'GET',
      shareToken,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000,
    }
  );

  return {
    entitySchema: data?.entity ?? null,
    error,
    isLoading,
    mutate,
  };
};

/**
 * Helper function to flatten nested field definitions into a flat list
 * with full paths (e.g., 'generalCorporationInfo.legalName')
 */
export function flattenSchemaFields(
  fields: Record<string, FieldDefinition>,
  parentPath = ''
): Array<{
  path: string;
  label: string;
  type: string;
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  enumOptions?: Array<{ value: string; label: string }>;
}> {
  const result: Array<{
    path: string;
    label: string;
    type: string;
    canRead: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    enumOptions?: Array<{ value: string; label: string }>;
  }> = [];

  Object.entries(fields).forEach(([fieldName, fieldDef]) => {
    const currentPath = parentPath ? `${parentPath}.${fieldName}` : fieldName;

    if (fieldDef.type === 'object' && fieldDef.fields) {
      // Recursively flatten nested fields
      result.push(...flattenSchemaFields(fieldDef.fields, currentPath));
    } else {
      result.push({
        path: fieldDef.read?.path ?? currentPath,
        label: fieldDef.label,
        type: fieldDef.type,
        canRead: !!fieldDef.read,
        canCreate: !!fieldDef.create,
        canUpdate: !!fieldDef.update,
        enumOptions: fieldDef.enumOptions,
      });
    }
  });

  return result;
}
